// Thin IndexedDB adapter for the engineer PWA, built on `idb`. Three stores:
//   jobs        -- the engineer's job list, cached while online for offline viewing
//   outbox      -- completions queued offline, drained on reconnect (see sync.ts)
//   photoOutbox -- job photos (binary Blobs) queued offline, uploaded on reconnect
// (Later sessions added the message outbox, the two read-only caches and, in Session APF, the
// sheetDrafts store: each is described beside its own adapter below.)
//
// Deliberately a thin, mechanical wrapper: all the sync/merge logic that could
// carry a bug lives in sync.ts / photoSync.ts (pure, unit-tested). This file persists.
import { openDB } from "idb";
import { offlineDatabaseName } from "../phonePreview";
import type { DBSchema, IDBPDatabase } from "idb";
import type { CachedJob, JobSheetDraft, OutboxItem, QueuedPhoto } from "./types";
import type { SurveyDraftRow, SurveyVisitDraftRow } from "../projectSurveyDraft";
import type { OutboxStore } from "./sync";
import type { PhotoOutboxStore } from "./photoSync";
import type { QueuedMessage, MessageOutboxStore } from "./messageSync";
import type { CachedHistoryEntry, HistoryCacheStore, PrunableHistoryCacheStore } from "./historyCache";

interface EleDB extends DBSchema {
  jobs: { key: string; value: CachedJob };
  outbox: { key: string; value: OutboxItem };
  photoOutbox: { key: string; value: QueuedPhoto; indexes: { byJob: string } };
  messageOutbox: { key: string; value: QueuedMessage };
  historyCache: { key: string; value: CachedHistoryEntry };
  timesheetCache: { key: string; value: CachedHistoryEntry };
  sheetDrafts: { key: string; value: JobSheetDraft; indexes: { bySavedAt: string } };
  surveyDrafts: { key: string; value: SurveyDraftRow; indexes: { byJob: string } };
  surveyVisits: { key: string; value: SurveyVisitDraftRow };
}

// Inside the Phone view's frame (Session BDZ) the offline stores live in a DATABASE OF THEIR OWN, so a sheet, photo or
// message queued by a page being reviewed there is never replayed by the real engineer app on this device, and a
// sign-out wipe in the frame cannot empty the real one. Everywhere else the name is exactly what it always was.
const DB_NAME = offlineDatabaseName("ele-engineer");
// v2 adds the photoOutbox store (job photos queued for upload). v3 adds the messageOutbox store
// (engineer replies queued offline, Session ACZ). v4 adds the historyCache store (company-wide
// site and lift history, Session AEK). v5 adds the timesheetCache store (the engineer's own weekly
// timesheet, Session AEL). v6 adds the sheetDrafts store (the in-progress job sheet, one per job,
// Session APF). v7 adds the surveyDrafts and surveyVisits stores (a project survey visit in progress, Session PJ2). Each
// upgrade is additive, so an existing device keeps its cached jobs, pending completions and queued photos.
const DB_VERSION = 7;

let dbPromise: Promise<IDBPDatabase<EleDB>> | null = null;

function getDb(): Promise<IDBPDatabase<EleDB>> {
  if (!dbPromise) {
    dbPromise = openDB<EleDB>(DB_NAME, DB_VERSION, {
      // An older tab holding the previous version blocks the upgrade, and because EVERY outbox
      // awaits this same connection, a block would wedge the engineer's sync, not just the screen
      // that triggered it. Closing this connection when a newer version wants in lets the upgrade
      // proceed instead of both sides waiting.
      blocking(_cv, _nv, event) {
        (event.target as IDBDatabase | null)?.close();
        dbPromise = null;
      },
      upgrade(db) {
        if (!db.objectStoreNames.contains("jobs")) db.createObjectStore("jobs", { keyPath: "id" });
        if (!db.objectStoreNames.contains("outbox")) db.createObjectStore("outbox", { keyPath: "id" });
        if (!db.objectStoreNames.contains("photoOutbox")) {
          const store = db.createObjectStore("photoOutbox", { keyPath: "id" });
          store.createIndex("byJob", "jobId");
        }
        if (!db.objectStoreNames.contains("messageOutbox")) db.createObjectStore("messageOutbox", { keyPath: "id" });
        if (!db.objectStoreNames.contains("historyCache")) db.createObjectStore("historyCache", { keyPath: "key" });
        if (!db.objectStoreNames.contains("timesheetCache")) db.createObjectStore("timesheetCache", { keyPath: "key" });
        if (!db.objectStoreNames.contains("sheetDrafts")) {
          // Keyed by job, so a job has at most one draft and saving again replaces it. The index is
          // on the ISO savedAt stamp (toISOString is fixed-width UTC, so it sorts in time order),
          // which is what lets the stale sweep find week-old rows without reading every signature.
          const store = db.createObjectStore("sheetDrafts", { keyPath: "jobId" });
          store.createIndex("bySavedAt", "savedAt");
        }
        // Session PJ2. One row per survey (the cells this phone changed), and one per visit (the lifts added on site,
        // the outcomes chosen, the times). Additive: no existing store is touched, so every queued item survives.
        if (!db.objectStoreNames.contains("surveyDrafts")) {
          const store = db.createObjectStore("surveyDrafts", { keyPath: "surveyId" });
          store.createIndex("byJob", "jobId");
        }
        if (!db.objectStoreNames.contains("surveyVisits")) db.createObjectStore("surveyVisits", { keyPath: "jobId" });
      },
    });
  }
  return dbPromise;
}

// ---- Jobs cache ----
export async function cacheJobs(jobs: CachedJob[]): Promise<void> {
  const db = await getDb();
  const tx = db.transaction("jobs", "readwrite");
  await tx.store.clear(); // replace wholesale: the server list is authoritative
  await Promise.all(jobs.map((j) => tx.store.put(j)));
  await tx.done;
}

export async function getCachedJobs(): Promise<CachedJob[]> {
  const db = await getDb();
  return db.getAll("jobs");
}

export async function getCachedJob(id: string): Promise<CachedJob | undefined> {
  const db = await getDb();
  return db.get("jobs", id);
}

// Change ONE cached job in a single transaction, leaving every other job alone. cacheJobs above replaces
// the whole list because the server's list is authoritative; this is for a change the device itself
// learned of (Session APG: a POP tag tap made from the Home screen) that has to be visible to the job
// sheet before the next refresh, and while there is no signal to refresh with.
//
// READ AND WRITE IN ONE READWRITE TRANSACTION, deliberately. Two separate ones (a get, then a put) leave a
// gap in which a refresh can clear and rewrite the store, and the put would then write back a stale copy
// of this one job (an old status, an old site) until the next refresh. Inside one transaction the refresh
// waits its turn and this sees the job it is really changing. `update` returns the same object when it
// has nothing to change, and nothing is written then. A job that is no longer cached is left absent.
export async function updateCachedJob(id: string, update: (job: CachedJob) => CachedJob): Promise<void> {
  const db = await getDb();
  const tx = db.transaction("jobs", "readwrite");
  const job = await tx.store.get(id);
  if (job) {
    const next = update(job);
    if (next !== job) await tx.store.put(next);
  }
  await tx.done;
}

// Drop the cached job list (Session AFW). Called on an auth change (sign-out / a different engineer
// signing in) so cached site ACCESS DETAILS (door codes, key locations) do not outlive the session
// on a shared/lost device, where the unguarded "Continue offline" view would otherwise read them. It
// is re-fetchable reference data, cleared alongside the history + timesheet caches; the engineer's
// own queued work (the outboxes) is deliberately NOT touched here.
export async function clearCachedJobs(): Promise<void> {
  const db = await getDb();
  await db.clear("jobs");
}

// ---- Outbox (implements the OutboxStore contract sync.ts drains) ----
export const outboxStore: OutboxStore = {
  async getOutbox() {
    const db = await getDb();
    return db.getAll("outbox");
  },
  async putOutbox(item: OutboxItem) {
    const db = await getDb();
    await db.put("outbox", item);
  },
  async deleteOutbox(id: string) {
    const db = await getDb();
    await db.delete("outbox", id);
  },
};

// ---- Photo outbox (implements the PhotoOutboxStore contract photoSync.ts drains) ----
export const photoOutboxStore: PhotoOutboxStore = {
  async getPhotoOutbox() {
    const db = await getDb();
    return db.getAll("photoOutbox");
  },
  async putPhoto(item: QueuedPhoto) {
    const db = await getDb();
    await db.put("photoOutbox", item);
  },
  async deletePhoto(id: string) {
    const db = await getDb();
    await db.delete("photoOutbox", id);
  },
};

// Photos queued for one job (for the JobDetail photos UI to show pending tiles).
export async function getQueuedPhotosForJob(jobId: string): Promise<QueuedPhoto[]> {
  const db = await getDb();
  return db.getAllFromIndex("photoOutbox", "byJob", jobId);
}

// ---- Message outbox (implements the MessageOutboxStore contract messageSync.ts drains) ----
export const messageOutboxStore: MessageOutboxStore = {
  async getMessageOutbox() {
    const db = await getDb();
    return db.getAll("messageOutbox");
  },
  async putMessage(item: QueuedMessage) {
    const db = await getDb();
    await db.put("messageOutbox", item);
  },
  async deleteMessage(id: string) {
    const db = await getDb();
    await db.delete("messageOutbox", id);
  },
};

// ---- History cache (implements the HistoryCacheStore contract historyCache.ts reads) ----
// Site and lift history (Session AEK; since Session ASB, D-SEC-6, only the sites where the engineer
// holds open work). Read-only reference data, so unlike the three outboxes it is DISPOSABLE: it is
// keyed by request, carries the time it was fetched, and is re-fetched once stale. Nothing here is the
// engineer's own work, so nothing here must survive. Walkable (listEntries) so a successful site-list
// sync can prune the pages of every site that has left scope.
export const historyCacheStore: PrunableHistoryCacheStore = {
  async getEntry(key: string) {
    const db = await getDb();
    return db.get("historyCache", key);
  },
  async putEntry(entry: CachedHistoryEntry) {
    const db = await getDb();
    await db.put("historyCache", entry);
  },
  async deleteEntry(key: string) {
    const db = await getDb();
    await db.delete("historyCache", key);
  },
  async listEntries() {
    const db = await getDb();
    return db.getAll("historyCache");
  },
  async clear() {
    const db = await getDb();
    await db.clear("historyCache");
  },
};

// ---- Timesheet cache (implements the same HistoryCacheStore contract, for the engineer's own
// weekly timesheet, Session AEL). The engineer can only VIEW it (the office edits it), so it is
// disposable read-only reference data exactly like the history cache above, just in its own store
// so a wipe of one never disturbs the other.
export const timesheetCacheStore: HistoryCacheStore = {
  async getEntry(key: string) {
    const db = await getDb();
    return db.get("timesheetCache", key);
  },
  async putEntry(entry: CachedHistoryEntry) {
    const db = await getDb();
    await db.put("timesheetCache", entry);
  },
  async deleteEntry(key: string) {
    const db = await getDb();
    await db.delete("timesheetCache", key);
  },
  async clear() {
    const db = await getDb();
    await db.clear("timesheetCache");
  },
};

// ---- Job sheet drafts (Session APF; read and written only through draftStore.ts) ----
// The in-progress job sheet, one row per job, so that leaving the sheet does not lose it.
//
// WHERE THIS SITS BETWEEN THE OTHER STORES. It is the engineer's OWN unfinished work, exactly like
// the three outboxes, so it is deliberately NOT disposable and there is deliberately no clear()
// here for an auth change to call: src/lib/offlineCache.ts explains that rule for the outboxes, and
// it applies to a draft for a sharper reason still. The session cookie is a fixed seven days and
// can run out in the middle of a visit, so clearing drafts on sign-out would destroy the sheet at
// exactly the moment the engineer is sent to sign in again. But unlike an outbox item it is never
// sent anywhere, so nothing drains it: a draft leaves this store when its sheet is submitted or
// its job declined, when the job turns out to be finished already, or when it has sat untouched
// for a week (deleteDraftsSavedBefore, and loadDraft's own check on the row it reads).
export interface SheetDraftStore {
  getDraft(jobId: string): Promise<JobSheetDraft | undefined>;
  putDraft(draft: JobSheetDraft): Promise<void>;
  deleteDraft(jobId: string): Promise<void>;
  /** Delete every draft last saved before the given ISO instant. Resolves to how many went. */
  deleteDraftsSavedBefore(cutoffIso: string): Promise<number>;
}

export const sheetDraftStore: SheetDraftStore = {
  async getDraft(jobId: string) {
    const db = await getDb();
    return db.get("sheetDrafts", jobId);
  },
  async putDraft(draft: JobSheetDraft) {
    const db = await getDb();
    await db.put("sheetDrafts", draft);
  },
  async deleteDraft(jobId: string) {
    const db = await getDb();
    await db.delete("sheetDrafts", jobId);
  },
  async deleteDraftsSavedBefore(cutoffIso: string) {
    const db = await getDb();
    const tx = db.transaction("sheetDrafts", "readwrite");
    // Keys only, through the index: a draft can carry a signature image, and there is no reason to
    // read one into memory just to throw it away.
    const keys = await tx.store.index("bySavedAt").getAllKeys(IDBKeyRange.upperBound(cutoffIso, true));
    await Promise.all(keys.map((key) => tx.store.delete(key)));
    await tx.done;
    return keys.length;
  },
};

// ---- Project survey drafts (Session PJ2; read and written only through surveyStore.ts) ----
// A survey visit in progress: the cells changed on this phone (per survey) and the visit's own choices (per job). Like
// sheetDrafts, this is the engineer's OWN unfinished work, so there is deliberately no clear() here for a sign-out to call:
// a session can end in the middle of a visit. A row leaves when the visit is submitted (the completion payload carries it).
// It holds no customer contact and no access note: those are server data, re-read with the visit.
export interface SurveyDraftStore {
  getSurvey(surveyId: string): Promise<SurveyDraftRow | undefined>;
  getSurveysForJob(jobId: string): Promise<SurveyDraftRow[]>;
  putSurvey(row: SurveyDraftRow): Promise<void>;
  deleteSurvey(surveyId: string): Promise<void>;
  getVisit(jobId: string): Promise<SurveyVisitDraftRow | undefined>;
  putVisit(row: SurveyVisitDraftRow): Promise<void>;
  deleteVisit(jobId: string): Promise<void>;
}

export const surveyDraftStore: SurveyDraftStore = {
  async getSurvey(surveyId: string) {
    const db = await getDb();
    return db.get("surveyDrafts", surveyId);
  },
  async getSurveysForJob(jobId: string) {
    const db = await getDb();
    return db.getAllFromIndex("surveyDrafts", "byJob", jobId);
  },
  async putSurvey(row: SurveyDraftRow) {
    const db = await getDb();
    await db.put("surveyDrafts", row);
  },
  async deleteSurvey(surveyId: string) {
    const db = await getDb();
    await db.delete("surveyDrafts", surveyId);
  },
  async getVisit(jobId: string) {
    const db = await getDb();
    return db.get("surveyVisits", jobId);
  },
  async putVisit(row: SurveyVisitDraftRow) {
    const db = await getDb();
    await db.put("surveyVisits", row);
  },
  async deleteVisit(jobId: string) {
    const db = await getDb();
    await db.delete("surveyVisits", jobId);
  },
};
